"use client";

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

export interface RfqItem {
  slug: string;
  sku: string;
  name: string;
  category: string;
  quantity: number;
  targetPrice?: string;
  remarks?: string;
  priceRange?: string;
}

interface RfqContextType {
  items: RfqItem[];
  itemCount: number;
  isOpen: boolean;
  openBasket: () => void;
  closeBasket: () => void;
  toggleBasket: () => void;
  addItem: (item: Omit<RfqItem, "quantity"> & { quantity?: number }) => void;
  removeItem: (slug: string) => void;
  updateQuantity: (slug: string, qty: number) => void;
  updateRemarks: (slug: string, remarks: string) => void;
  clearBasket: () => void;
}

const RfqBasketContext = createContext<RfqContextType | null>(null);

const STORAGE_KEY = "wfd_rfq_basket";

export function RfqBasketProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<RfqItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setItems(JSON.parse(saved));
      }
    } catch (e) {
      console.error("Failed to load RFQ basket:", e);
    }
    setLoaded(true);
  }, []);

  // Save to localStorage on change
  useEffect(() => {
    if (!loaded) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error("Failed to save RFQ basket:", e);
    }
  }, [items, loaded]);

  const addItem = useCallback((item: Omit<RfqItem, "quantity"> & { quantity?: number }) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.slug === item.slug);
      if (existing) {
        return prev.map((i) =>
          i.slug === item.slug ? { ...i, quantity: i.quantity + (item.quantity || 1) } : i
        );
      }
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((slug: string) => {
    setItems((prev) => prev.filter((i) => i.slug !== slug));
  }, []);

  const updateQuantity = useCallback((slug: string, qty: number) => {
    if (qty < 1) return;
    setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, quantity: qty } : i)));
  }, []);

  const updateRemarks = useCallback((slug: string, remarks: string) => {
    setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, remarks } : i)));
  }, []);

  const clearBasket = useCallback(() => {
    setItems([]);
  }, []);

  const openBasket = useCallback(() => setIsOpen(true), []);
  const closeBasket = useCallback(() => setIsOpen(false), []);
  const toggleBasket = useCallback(() => setIsOpen((v) => !v), []);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <RfqBasketContext.Provider
      value={{
        items,
        itemCount,
        isOpen,
        openBasket,
        closeBasket,
        toggleBasket,
        addItem,
        removeItem,
        updateQuantity,
        updateRemarks,
        clearBasket,
      }}
    >
      {children}
    </RfqBasketContext.Provider>
  );
}

export function useRfqBasket() {
  const ctx = useContext(RfqBasketContext);
  if (!ctx) {
    // Return safe defaults when used outside provider (SSR / etc)
    return {
      items: [],
      itemCount: 0,
      isOpen: false,
      openBasket: () => {},
      closeBasket: () => {},
      toggleBasket: () => {},
      addItem: () => {},
      removeItem: () => {},
      updateQuantity: () => {},
      updateRemarks: () => {},
      clearBasket: () => {},
    };
  }
  return ctx;
}
